import React, { useState, useEffect, useCallback } from 'react';
import { therapistApi } from '../../services/api';
import { Therapist } from '../../types';
import TherapistForm from './TherapistForm';
import { handleApiError, handleSuccess } from '../../utils/errorHandler';
import './TherapistList.css';

const TherapistList: React.FC = () => {
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState<Therapist | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTherapists = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading therapists...');
      const result = await therapistApi.getAll({
        search: searchTerm.trim() || undefined,
        active: activeFilter,
        limit: 50
      });
      
      console.log('✅ API Response:', result);
      
      if (result && result.data && result.data.therapists) {
        console.log('✅ Setting therapists:', result.data.therapists.length);
        setTherapists(result.data.therapists);
      } else {
        console.log('❌ No therapists in response');
      }
    } catch (err: any) {
      console.log('❌ Error loading therapists:', err);
      setError(err.error || 'Failed to load therapists');
      handleApiError(err, 'Failed to load therapists');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTherapists();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [loadTherapists]);

  const handleEdit = (therapist: Therapist) => {
    setEditingTherapist(therapist);
    setShowForm(true);
  };

  const handleDelete = async (therapist: Therapist) => {
    const isActive = therapist.is_active;
    const action = isActive ? 'deactivate' : 'permanently delete';
    const confirmMessage = isActive 
      ? `Are you sure you want to deactivate ${therapist.name}? They will be moved to inactive status.`
      : `Are you sure you want to permanently delete ${therapist.name}? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        await therapistApi.delete(therapist.id);
        const successMessage = isActive 
          ? 'Therapist deactivated successfully' 
          : 'Therapist permanently deleted';
        handleSuccess(successMessage);
        loadTherapists();
      } catch (error: any) {
        // Handle specific error for therapists with assignment history
        if (error.error?.includes('assignment history')) {
          const forceDelete = window.confirm(
            `${therapist.name} has assignment history. Do you want to permanently delete anyway? This will remove all historical data.`
          );
          if (forceDelete) {
            try {
              // Add force parameter to the API call
              await therapistApi.delete(therapist.id, true);
              handleSuccess('Therapist permanently deleted');
              loadTherapists();
            } catch (forceError) {
              handleApiError(forceError as any, 'Failed to delete therapist');
            }
          }
        } else {
          handleApiError(error, `Failed to ${action} therapist`);
        }
      }
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingTherapist(null);
    // Clear search and reset filters to show all therapists including the new one
    setSearchTerm('');
    setActiveFilter(true);
    // Small delay to ensure the form state is cleared before reloading
    setTimeout(() => {
      loadTherapists();
    }, 100);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTherapist(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading therapists...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading therapists: {error}</p>
        <button onClick={loadTherapists} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="therapist-list">
      <div className="therapist-list-header">
        <h2>Therapists</h2>
        <button 
          className="add-button"
          onClick={() => setShowForm(true)}
        >
          Add Therapist
        </button>
      </div>

      <div className="therapist-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search therapists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-container">
          <label>
            <input
              type="radio"
              name="activeFilter"
              checked={activeFilter === true}
              onChange={() => setActiveFilter(true)}
            />
            Active Only
          </label>
          <label>
            <input
              type="radio"
              name="activeFilter"
              checked={activeFilter === undefined}
              onChange={() => setActiveFilter(undefined)}
            />
            All
          </label>
        </div>
      </div>

      <div className="therapist-grid">
        {therapists.map((therapist) => (
          <div key={therapist.id} className="therapist-card">
            <div className="therapist-header">
              <div className="therapist-info">
                <h3>{therapist.name}</h3>
                <p className="specialization">{therapist.specialization}</p>
              </div>
              <div className={`status-badge ${therapist.is_active ? 'active' : 'inactive'}`}>
                {therapist.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            
            <div className="therapist-details">
              {therapist.qualification && (
                <p><strong>Qualification:</strong> {therapist.qualification}</p>
              )}
              {therapist.designation && (
                <p><strong>Designation:</strong> {therapist.designation}</p>
              )}
              {therapist.email && (
                <p><strong>Email:</strong> {therapist.email}</p>
              )}
              {therapist.phone && (
                <p><strong>Phone:</strong> {therapist.phone}</p>
              )}
            </div>

            <div className="therapist-actions">
              <button 
                className="edit-button"
                onClick={() => handleEdit(therapist)}
              >
                Edit
              </button>
              <button 
                className={`delete-button ${therapist.is_active ? 'deactivate' : 'permanent-delete'}`}
                onClick={() => handleDelete(therapist)}
              >
                {therapist.is_active ? 'Deactivate' : 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {therapists.length === 0 && (
        <div className="empty-state">
          <p>No therapists found.</p>
          <button 
            className="add-button"
            onClick={() => setShowForm(true)}
          >
            Add First Therapist
          </button>
        </div>
      )}

      {showForm && (
        <TherapistForm
          therapist={editingTherapist}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default TherapistList;